import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { ShellLayout } from '@/features/shell/ShellLayout';
import { AuthLayout } from '@/features/auth/AuthLayout';
import { ScopedAuthLayout } from '@/features/auth/ScopedAuthLayout';
import { LoginPage } from '@/features/auth/LoginPage';
import { PageSkeleton } from '@/shared/ui/PageSkeleton';
import { RouteErrorPage } from '@/features/shell/RouteErrorPage';
import { LegacyRedirect } from './LegacyRedirect';
import { ToastProvider } from './providers/ToastProvider';
import { publicRoutes, scopedRoutes } from './routes';

// Lazy-load бүх feature page (route бүрд тусдаа chunk).
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const RoomsPage = lazy(() => import('@/features/rooms/RoomsPage'));
const ItemsPage = lazy(() => import('@/features/items/ItemsPage'));
const CatalogsPage = lazy(() => import('@/features/catalog/CatalogsPage'));
const CatalogEditPage = lazy(() => import('@/features/catalog/CatalogEditPage'));
const CatalogOrdersPage = lazy(() => import('@/features/catalog/CatalogOrdersPage'));
const ShopStorePage = lazy(() => import('@/features/shop/ShopStorePage'));
const CatalogSharePage = lazy(() => import('@/features/catalog-public/CatalogSharePage'));
const InvoicePublicViewPage = lazy(() => import('@/features/sales/InvoicePublicViewPage'));
const CustomersPage = lazy(() => import('@/features/customers/CustomersPage'));
const VendorsPage = lazy(() => import('@/features/vendors/VendorsPage'));
const PurchasesPage = lazy(() => import('@/features/purchases/PurchasesPage'));
const PurchaseEditPage = lazy(() => import('@/features/purchases/PurchaseEditPage'));
const PurchaseReturnsPage = lazy(() => import('@/features/purchase-returns/PurchaseReturnsPage'));
const PurchaseReturnEditPage = lazy(() => import('@/features/purchase-returns/PurchaseReturnEditPage'));
const SalesPage = lazy(() => import('@/features/sales/SalesPage'));
const SaleEditPage = lazy(() => import('@/features/sales/SaleEditPage'));
const SalesReturnsPage = lazy(() => import('@/features/sales-returns/SalesReturnsPage'));
const SalesReturnEditPage = lazy(() => import('@/features/sales-returns/SalesReturnEditPage'));
const SalePrintPage = lazy(() => import('@/features/sales/SalePrintPage'));
const PurchasePrintPage = lazy(() => import('@/features/purchases/PurchasePrintPage'));
const ArPage = lazy(() => import('@/features/ar/ArPage'));
const ArEditPage = lazy(() => import('@/features/ar/ArEditPage'));
const ApPage = lazy(() => import('@/features/ap/ApPage'));
const ApEditPage = lazy(() => import('@/features/ap/ApEditPage'));
const InvoicesPage = lazy(() => import('@/features/invoice/InvoicesPage'));
const InvoiceDetailPage = lazy(() => import('@/features/invoice/InvoiceDetailPage'));
const ProfilePage = lazy(() => import('@/features/tenant/ProfilePage'));
const CompanySettingsPage = lazy(() => import('@/features/tenant/CompanySettingsPage'));
const TerminalsPage = lazy(() => import('@/features/tenant/TerminalsPage'));
const ItemQtyReportPage = lazy(() => import('@/features/reports/ItemQtyReportPage'));
const PurchaseReportPage = lazy(() => import('@/features/reports/PurchaseReportPage'));
const ProfitReportPage = lazy(() => import('@/features/reports/ProfitReportPage'));
const AgingReportPage = lazy(() => import('@/features/reports/AgingReportPage'));
const TxHistoryReportPage = lazy(() => import('@/features/reports/TxHistoryReportPage'));
const ChartOfAccountsPage = lazy(() => import('@/features/gl/ChartOfAccountsPage'));
const JournalsPage = lazy(() => import('@/features/gl/JournalsPage'));
const JournalEditPage = lazy(() => import('@/features/gl/JournalEditPage'));
const FiscalPeriodsPage = lazy(() => import('@/features/gl/FiscalPeriodsPage'));
const SalesHistoryPage = lazy(() => import('@/features/sales/SalesHistoryPage'));
const SalesSummaryPage = lazy(() => import('@/features/sales/SalesSummaryPage'));
const PriceGroupsPage = lazy(() => import('@/features/pricing/PriceGroupsPage'));
const BankAccountsPage = lazy(() => import('@/features/bankAccounts/BankAccountsPage'));
const TicketsPage = lazy(() => import('@/features/pricing/TicketsPage'));
const RoadmapPage = lazy(() => import('@/features/pricing/RoadmapPage'));
const PosPage = lazy(() => import('@/features/pos/PosPage'));
const PosShiftsPage = lazy(() => import('@/features/pos-shifts/PosShiftsPage'));
const CountsPage = lazy(() => import('@/features/counts/CountsPage'));
const CountEditPage = lazy(() => import('@/features/counts/CountEditPage'));
const InReturnsPage = lazy(() => import('@/features/inventory-returns/InReturnsPage'));
const InReturnEditPage = lazy(() => import('@/features/inventory-returns/InReturnEditPage'));
const TransfersPage = lazy(() => import('@/features/transfers/TransfersPage'));
const TransferEditPage = lazy(() => import('@/features/transfers/TransferEditPage'));
const LandingPage = lazy(() => import('@/features/landing/LandingPage'));
const SelectScopePage = lazy(
  () => import('@/features/select-scope/SelectScopePage'),
);

function Suspended({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={<PageSkeleton />}
    >
      {children}
    </Suspense>
  );
}

const SCOPE_PREFIX = '/:tenantId/:companyId';

export const router = createBrowserRouter([
  // Public landing
  { path: publicRoutes.landing, element: <Suspended><LandingPage /></Suspended> },

  // Public каталог share (auth-гүй, shell-гүй, scope-гүй)
  {
    path: publicRoutes.catalogShare,
    element: <Suspended><ToastProvider><CatalogSharePage /></ToastProvider></Suspended>,
  },

  // Public нэхэмжлэх view — InvoiceShareToken-аар (auth-гүй, shell-гүй).
  {
    path: publicRoutes.invoicePublic,
    element: <Suspended><InvoicePublicViewPage /></Suspended>,
  },

  // Auth + select-scope (no scope prefix)
  {
    element: <AuthLayout />,
    children: [
      { path: publicRoutes.login, element: <LoginPage /> },
      { path: publicRoutes.selectScope, element: <Suspended><SelectScopePage /></Suspended> },
    ],
  },

  // Scoped shell (бүх authenticated UI page)
  {
    path: SCOPE_PREFIX,
    element: <ShellLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: scopedRoutes.dashboard, element: <Suspended><DashboardPage /></Suspended> },
      { path: scopedRoutes.rooms, element: <Suspended><RoomsPage /></Suspended> },
      { path: scopedRoutes.items, element: <Suspended><ItemsPage /></Suspended> },
      { path: scopedRoutes.catalogs, element: <Suspended><CatalogsPage /></Suspended> },
      { path: scopedRoutes.catalogEdit, element: <Suspended><CatalogEditPage /></Suspended> },
      { path: scopedRoutes.catalogOrders, element: <Suspended><CatalogOrdersPage /></Suspended> },
      { path: scopedRoutes.shopStore, element: <Suspended><ShopStorePage /></Suspended> },
      { path: scopedRoutes.customers, element: <Suspended><CustomersPage /></Suspended> },
      { path: scopedRoutes.vendors, element: <Suspended><VendorsPage /></Suspended> },
      { path: scopedRoutes.purchases, element: <Suspended><PurchasesPage /></Suspended> },
      { path: scopedRoutes.purchaseEdit, element: <Suspended><PurchaseEditPage /></Suspended> },
      { path: scopedRoutes.purchaseReturns, element: <Suspended><PurchaseReturnsPage /></Suspended> },
      { path: scopedRoutes.purchaseReturnEdit, element: <Suspended><PurchaseReturnEditPage /></Suspended> },
      { path: scopedRoutes.sales, element: <Suspended><SalesPage /></Suspended> },
      { path: scopedRoutes.saleEdit, element: <Suspended><SaleEditPage /></Suspended> },
      { path: scopedRoutes.salesReturns, element: <Suspended><SalesReturnsPage /></Suspended> },
      { path: scopedRoutes.salesReturnEdit, element: <Suspended><SalesReturnEditPage /></Suspended> },
      { path: scopedRoutes.ar, element: <Suspended><ArPage /></Suspended> },
      { path: scopedRoutes.arEdit, element: <Suspended><ArEditPage /></Suspended> },
      { path: scopedRoutes.ap, element: <Suspended><ApPage /></Suspended> },
      { path: scopedRoutes.apEdit, element: <Suspended><ApEditPage /></Suspended> },
      { path: scopedRoutes.invoices, element: <Suspended><InvoicesPage /></Suspended> },
      { path: scopedRoutes.invoiceDetail, element: <Suspended><InvoiceDetailPage /></Suspended> },
      { path: scopedRoutes.profile, element: <Suspended><ProfilePage /></Suspended> },
      { path: scopedRoutes.companySettings, element: <Suspended><CompanySettingsPage /></Suspended> },
      { path: scopedRoutes.terminals, element: <Suspended><TerminalsPage /></Suspended> },
      { path: scopedRoutes.reportItemQty, element: <Suspended><ItemQtyReportPage /></Suspended> },
      { path: scopedRoutes.reportPurchase, element: <Suspended><PurchaseReportPage /></Suspended> },
      { path: scopedRoutes.reportProfit, element: <Suspended><ProfitReportPage /></Suspended> },
      { path: scopedRoutes.reportAging, element: <Suspended><AgingReportPage /></Suspended> },
      { path: scopedRoutes.reportTxHistory, element: <Suspended><TxHistoryReportPage /></Suspended> },
      { path: scopedRoutes.chartOfAccounts, element: <Suspended><ChartOfAccountsPage /></Suspended> },
      { path: scopedRoutes.glJournals, element: <Suspended><JournalsPage /></Suspended> },
      { path: scopedRoutes.glJournalEdit, element: <Suspended><JournalEditPage /></Suspended> },
      { path: scopedRoutes.fiscalPeriods, element: <Suspended><FiscalPeriodsPage /></Suspended> },
      { path: scopedRoutes.salesHistory, element: <Suspended><SalesHistoryPage /></Suspended> },
      { path: scopedRoutes.salesSummary, element: <Suspended><SalesSummaryPage /></Suspended> },
      { path: scopedRoutes.priceGroups, element: <Suspended><PriceGroupsPage /></Suspended> },
      { path: scopedRoutes.bankAccounts, element: <Suspended><BankAccountsPage /></Suspended> },
      { path: scopedRoutes.tickets, element: <Suspended><TicketsPage /></Suspended> },
      { path: scopedRoutes.roadmap, element: <Suspended><RoadmapPage /></Suspended> },
      { path: scopedRoutes.posShifts, element: <Suspended><PosShiftsPage /></Suspended> },
      { path: scopedRoutes.counts, element: <Suspended><CountsPage /></Suspended> },
      { path: scopedRoutes.countEdit, element: <Suspended><CountEditPage /></Suspended> },
      { path: scopedRoutes.inReturns, element: <Suspended><InReturnsPage /></Suspended> },
      { path: scopedRoutes.inReturnEdit, element: <Suspended><InReturnEditPage /></Suspended> },
      { path: scopedRoutes.transfers, element: <Suspended><TransfersPage /></Suspended> },
      { path: scopedRoutes.transferEdit, element: <Suspended><TransferEditPage /></Suspended> },
    ],
  },

  // Scoped auth-only (no shell): POS, Print
  {
    path: SCOPE_PREFIX,
    element: <ScopedAuthLayout />,
    children: [
      { path: scopedRoutes.pos, element: <Suspended><PosPage /></Suspended> },
      { path: scopedRoutes.salePrint, element: <Suspended><SalePrintPage /></Suspended> },
      { path: scopedRoutes.purchasePrint, element: <Suspended><PurchasePrintPage /></Suspended> },
    ],
  },

  // Legacy flat path → scoped redirect (хуучин bookmark-уудад зориулсан)
  { path: '/landing.html', element: <Navigate to={publicRoutes.landing} replace /> },
  { path: '/dashboard', element: <LegacyRedirect to="dashboard" /> },
  { path: '/dashboard.html', element: <LegacyRedirect to="dashboard" /> },
  { path: '/items', element: <LegacyRedirect to="items" /> },
  { path: '/items.html', element: <LegacyRedirect to="items" /> },
  { path: '/customers', element: <LegacyRedirect to="customers" /> },
  { path: '/customers.html', element: <LegacyRedirect to="customers" /> },
  { path: '/vendors', element: <LegacyRedirect to="vendors" /> },
  { path: '/vendors.html', element: <LegacyRedirect to="vendors" /> },
  { path: '/price-groups', element: <LegacyRedirect to="priceGroups" /> },
  { path: '/sales', element: <LegacyRedirect to="sales" /> },
  { path: '/sales.html', element: <LegacyRedirect to="sales" /> },
  { path: '/sale-edit', element: <LegacyRedirect to="saleEdit" /> },
  { path: '/sale-print', element: <LegacyRedirect to="salePrint" /> },
  { path: '/sales-history', element: <LegacyRedirect to="salesHistory" /> },
  { path: '/sales-summary', element: <LegacyRedirect to="salesSummary" /> },
  { path: '/purchases', element: <LegacyRedirect to="purchases" /> },
  { path: '/purchases.html', element: <LegacyRedirect to="purchases" /> },
  { path: '/purchase-edit', element: <LegacyRedirect to="purchaseEdit" /> },
  { path: '/purchase-print', element: <LegacyRedirect to="purchasePrint" /> },
  { path: '/ar', element: <LegacyRedirect to="ar" /> },
  { path: '/ar.html', element: <LegacyRedirect to="ar" /> },
  { path: '/ar-edit', element: <LegacyRedirect to="arEdit" /> },
  { path: '/ap', element: <LegacyRedirect to="ap" /> },
  { path: '/ap.html', element: <LegacyRedirect to="ap" /> },
  { path: '/ap-edit', element: <LegacyRedirect to="apEdit" /> },
  { path: '/report-item-qty', element: <LegacyRedirect to="reportItemQty" /> },
  { path: '/report-tx-history', element: <LegacyRedirect to="reportTxHistory" /> },
  { path: '/invoices', element: <LegacyRedirect to="invoices" /> },
  { path: '/invoices.html', element: <LegacyRedirect to="invoices" /> },
  { path: '/invoice-detail', element: <LegacyRedirect to="invoiceDetail" /> },
  { path: '/profile', element: <LegacyRedirect to="profile" /> },
  { path: '/profile.html', element: <LegacyRedirect to="profile" /> },
  { path: '/company-settings', element: <LegacyRedirect to="companySettings" /> },
  { path: '/terminals', element: <LegacyRedirect to="terminals" /> },
  { path: '/tickets', element: <LegacyRedirect to="tickets" /> },
  { path: '/roadmap', element: <LegacyRedirect to="roadmap" /> },
  { path: '/pos', element: <LegacyRedirect to="pos" /> },
  { path: '/pos.html', element: <LegacyRedirect to="pos" /> },
  { path: '/counts', element: <LegacyRedirect to="counts" /> },
  { path: '/count-edit', element: <LegacyRedirect to="countEdit" /> },
  { path: '/returns', element: <LegacyRedirect to="inReturns" /> },
  { path: '/return-edit', element: <LegacyRedirect to="inReturnEdit" /> },

  // Catch-all: landing
  { path: '*', element: <Navigate to={publicRoutes.landing} replace /> },
]);
